import React, { useState, useEffect, useMemo } from 'react';
import {
	Modal, ModalHeader, ModalBody, ModalFooter,
	Row, Col, Input, Label, Button, Card, CardBody, Badge
} from 'reactstrap';
import Select from 'react-select';
import { v4 as uuidv4 } from 'uuid';
import { withTranslation, WithTranslation } from 'react-i18next';
import Filter from './Filter/Filter';
import { STRATEGIES, Rule, AssignmentField, AssignmentInput, STRATEGY_HELPERS } from './AutoAssignTypes';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { isJson } from 'utils';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { Action } from 'redux';
import { ApplicationState } from 'store';
import DynamicAssignment from './DynamicAssignment';
import { roleListGetRequest } from 'store/role/action';
import { usersListGetRequest } from 'store/user/action';
import { teamsListGetRequest } from 'store/teams/action';

interface Props extends WithTranslation {
	isOpen: boolean;
	initialRule?: Rule;
	metaDataFields: any;
	assignmentFields: AssignmentField[];
	onSave: (rule: Rule) => void;
	onCancel: () => void;
}

const AutoAssignRuleWizard: React.FC<Props> = ({ isOpen, initialRule, metaDataFields, assignmentFields, onSave, onCancel, t }) => {
	const dispatch: ThunkDispatch<any, null, Action<string>> = useDispatch();
	const usersData = useSelector((state: ApplicationState) => state.user.users);
	const rolesData = useSelector((state: ApplicationState) => state.role.list);
	const teamsData = useSelector((state: ApplicationState) => state.teams.teams);
	const [step, setStep] = useState<0 | 1 | 2>(0);
	const [metaData, setMetaData]: any = useState<any>([]);
	const [columns, setColumns] = useState<any>([])
	const [users, setUsers] = useState<any>([])
	const [roles, setRoles] = useState<any>([])
	const [teams, setTeams] = useState<any>([])

	const blankAssignments = useMemo(
		() =>
			assignmentFields.reduce((acc, f) => {
				acc[f.key] = { type: 'users', values: [] };
				return acc;
			}, {} as Record<string, AssignmentInput>),
		[assignmentFields]
	);


	const [rule, setRule] = useState<Rule>({
		id: initialRule?.id || uuidv4(),
		name: initialRule?.name || '',
		priority: initialRule?.priority || 1,
		strategy: initialRule?.strategy || STRATEGIES[0].value,
		filters: initialRule?.filters || [],
		assignments: initialRule?.assignments || blankAssignments
	});


	useEffect(() => {
		const filters = { pageSize: 500 }
		dispatch(roleListGetRequest(filters, 0));
		let queryParams: any = new URLSearchParams({});
		queryParams.set('pageSize', 500)
		queryParams.set('page', 0)
		dispatch(usersListGetRequest(queryParams.toString()));
		dispatch(teamsListGetRequest(queryParams.toString()));
	}, [])


	useEffect(() => {
		if (usersData?.length) {
			const users = usersData.map((user: any) => ({ value: user.id, label: user.name }));
			setUsers(users);
		}
	}, [usersData])

	useEffect(() => {
		if (rolesData?.length) {
			const roles = rolesData.map((role: any) => ({ value: role.id, label: role.title }));
			setRoles(roles);
		}
	}, [rolesData])

	useEffect(() => {
		if (teamsData?.length) {
			const teams = teamsData.map((team: any) => ({ value: team.id, label: team.name }));
			setTeams(teams)
		}
	}, [teamsData])

	// Update metadata fields
	useEffect(() => {
		let index = -1;
		let dataFields = metaDataFields
		dataFields?.length && dataFields?.map((field: any, i: any) => {
			let item = field &&
				field?.valuesJson && isJson(field.valuesJson)
				? JSON.parse(field.valuesJson)
				: field.valuesJson;
			if (item?.key === 'title') return index = i;
			const elem = dataFields.splice(index, 1)[0];
			dataFields.unshift(elem);
		});
		setMetaData(dataFields);
	}, [metaDataFields]);

	useEffect(() => {
		let columns: any[] = [];
		if (metaData && metaData.length > 0) {
			metaData.map((fieldName: any) => {
				let fields = fieldName.valuesJson && isJson(fieldName.valuesJson) ? JSON.parse(fieldName.valuesJson) : fieldName?.valuesJson ? fieldName?.valuesJson : {}
				let newColumn = {
					Header: _.startCase(fields.label),
					accessor: fields.key,
					id: fields.key,
					fieldid: fieldName.id,
					filterable: true,
					isSortable: false,
					disableFilters: true,
					valueJson: fields,
					meta: fieldName,
					show: fields.tablecolumn,
					isPrimary: fields.isPrimary,
				}
				columns.push(newColumn)
			});
		}
		setColumns(columns)
	}, [metaData]);

	useEffect(() => {
		if (isOpen) {
			setStep(0);
			setRule({
				id: initialRule?.id || uuidv4(),
				name: initialRule?.name || '',
				priority: initialRule?.priority || 1,
				strategy: initialRule?.strategy || STRATEGIES[0].value,
				filters: initialRule?.filters || [],
				assignments: initialRule?.assignments || blankAssignments,
			});
		}
	}, [isOpen, initialRule, blankAssignments]);

	const stepper = [
		{ icon: 'ri-edit-box-line', label: t('properties.auto_assign.details') },
		{ icon: 'ri-filter-3-line', label: t('common.table.filters') },
		{ icon: 'ri-user-settings-line', label: t('properties.auto_assign.assignments') },
	];

	const renderStepper = () => (
		<div className="px-4 py-3">
			<div className="d-flex justify-content-between align-items-start gap-4">
				{stepper.map((stepInfo, index) => {
					const isCompleted = index < step;
					const isActive = index === step;

					const icon = isCompleted
						? <i className="ri-check-line" />
						: <i className={stepInfo.icon} />;

					const circleClass = isCompleted
						? 'bg-success text-white'
						: isActive
							? 'bg-primary text-white'
							: 'bg-white border border-secondary text-secondary';

					return (
						<div key={index} className="text-center flex-fill d-flex flex-column align-items-center" onClick={() => setStep(index as 0 | 1 | 2)} style={{ cursor: 'pointer' }}>
							<div
								className={`rounded-circle shadow-sm d-flex align-items-center justify-content-center ${circleClass}`}
								style={{
									width: 48,
									height: 48,
									fontSize: 20,
									transition: 'all 0.2s ease',
								}}
							>
								{icon}
							</div>
							<div
								className={`mt-2 small ${isCompleted ? 'text-success' : isActive ? 'text-primary fw-semibold' : 'text-muted'}`}
								style={{ fontSize: '0.85rem' }}
							>
								{stepInfo.label}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);


	const renderSummary = () => (
		<Card className="sticky-top shadow-sm border rounded-4 p-4" style={{ top: 80 }}>
			<Label><h6 className="fw-bold mb-4 border-bottom pb-2">{t('properties.auto_assign.summary')}</h6></Label>

			<div className="mb-3 d-flex align-items-start">
				<div className="me-3 bg-primary-subtle text-warning-emphasis rounded-circle d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
					<i className="ri-edit-line text-primary fs-5" />
				</div>
				<div>
					<small className="text-muted d-block">{t('properties.auto_assign.rule_name')}</small>
					<div className="fw-semibold">{rule.name || <em>{t('properties.auto_assign.unnamed')}</em>}</div>
				</div>
			</div>

			<div className="mb-3 d-flex align-items-start">
				<div className="me-3 bg-info-subtle text-warning-emphasis rounded-circle d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
					<i className="ri-shuffle-line text-info fs-5" />
				</div>

				<div>
					{t(STRATEGIES.find(s => s.value === rule.strategy)?.label || '')}
					<br />
					<small className="text-muted">{t(STRATEGY_HELPERS[rule.strategy] || '')}</small>
				</div>

			</div>{/* ── Filters Summary ─────────────────────────────── */}
			<div className="mb-4 d-flex align-items-start">
				<div className="me-3">
					<div className="bg-warning-subtle text-warning-emphasis rounded-circle d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
						<i className="ri-search-2-line fs-5" />
					</div>
				</div>
				<div className="flex-grow-1">
					<div className="d-flex justify-content-between align-items-center">
						<small className="text-muted fw-semibold">{t('common.table.filters')}</small>
						<Badge color={rule.filters?.length ? 'info' : 'secondary'} pill>
							{rule.filters?.length || 0}
						</Badge>
					</div>

					{rule.filters?.length === 0 ? (
						<small className="text-danger d-block mt-1">
							<i className="ri-error-warning-line me-1" />
							{t('common.table.no_filters_defined')}
						</small>
					) : (
						rule.filters.map((group, gIndex) => (
							<div key={gIndex} className="border-start ps-2 mt-2">
								<div className="fw-medium small text-muted mb-1">
									{t('common.table.group')} {gIndex + 1}
								</div>
								{group.filters.map((f: any, fIdx: number) => (
									<div key={fIdx} className="text-muted small d-flex align-items-start mb-1">
										<i className="ri-subtract-line text-muted me-1 pt-1" />
										<div>
											<span className="fw-semibold">{f.keyLabel}</span> {f.conditionLabel}{' '}
											{f.valuesLabel?.map((v: any, i: number) => (
												<span key={i}>
													<span className="badge bg-light text-dark me-1">{v.label}</span>
												</span>
											))}
											{f.value && f.highValue && (
												<span className="badge bg-light text-dark">{f.value} → {f.highValue}</span>
											)}
										</div>
									</div>
								))}
							</div>
						))
					)}
				</div>
			</div>

			{/* ── Assignments Summary ─────────────────────────── */}
			<div className="mb-4 d-flex align-items-start">
				<div className="me-3">
					<div className="bg-success-subtle text-success-emphasis rounded-circle d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
						<i className="ri-user-settings-line fs-5" />
					</div>
				</div>

				<div className="flex-grow-1">
					<div className="d-flex justify-content-between align-items-center">
						<small className="text-muted fw-semibold">{t('properties.auto_assign.assignments')}</small>
						<Badge
							color={
								Object.values(rule.assignments).some(a => a.values.length)
									? 'success'
									: 'secondary'
							}
							pill
						>
							{
								Object.values(rule.assignments).reduce(
									(acc, a) => acc + (a.values?.length || 0),
									0
								)
							}
						</Badge>
					</div>

					{Object.entries(rule.assignments)
						.filter(([_, a]) => a.values.length > 0)
						.map(([key, assignment]) => {
							const fieldLabel = assignmentFields.find(f => f.key === key)?.label || key;
							const modeLabel = {
								users: t("By User"),
								roles: t("By Role"),
								teams: t("By Team"),
							}[assignment.type];

							return (
								<div key={key} className="mt-2 ps-2 border-start">
									<div className="text-muted small mb-1 fw-semibold">
										{fieldLabel} <span className="text-secondary">({modeLabel})</span>
									</div>
									<div className="d-flex flex-wrap gap-2">
										{assignment.values.map((val, idx) => (
											<Badge color="light" className="text-body"> {val.label} </Badge>
										))}
									</div>
								</div>
							);
						})}

					{Object.values(rule.assignments).every(a => !a.values.length) && (
						<small className="text-danger d-block mt-1">
							<i className="ri-error-warning-line me-1" />
							{t('properties.auto_assign.no_assignments_defined')}
						</small>
					)}
				</div>
			</div>



		</Card>
	);
	return (
		<Modal isOpen={isOpen} fullscreen toggle={onCancel} className="modal-premium">
			<ModalHeader
				toggle={onCancel}
				className="border-bottom px-4 py-3 shadow-sm d-flex align-items-center"
				style={{ fontSize: '1.1rem', fontWeight: 600 }}
			>
				{initialRule ? t('properties.auto_assign.edit_rule') : t('properties.auto_assign.create_auto_assignment_rule')}
			</ModalHeader>

			<ModalBody className="px-4 py-3">
				{renderStepper()}
				<Row className="gx-4">
					<Col lg={8}>
						<Card className="shadow-sm border rounded-4 p-4">
							<CardBody className="p-0">
								{step === 0 && (
									<>
										<div className="mb-4">
											<Label className="form-label fw-semibold">{t('properties.auto_assign.rule_name')}</Label>
											<small className="form-text text-muted mb-2 d-block">{t('properties.auto_assign.visible_to_your_team_and_used_for_reference')}</small>
											<Input
												className="form-control"
												value={rule.name}
												placeholder={t('properties.auto_assign.eg_assign_leads_to_counsellor')}
												onChange={(e) => setRule(r => ({ ...r, name: e.target.value }))}
											/>
										</div>

										<div className="mb-4">
											<Label className="form-label fw-semibold">{t('properties.auto_assign.strategy')}</Label>
											<small className="form-text text-muted mb-2 d-block">{t('properties.auto_assign.select_how_the_assignment_should_happen')}</small>
											<Select
												options={t('STRATEGIES') ? STRATEGIES.map(s => ({ value: s.value, label: t(s.label) })) : STRATEGIES}
												styles
												value={
													STRATEGIES.find(s => s.value === rule.strategy)
														? {
															value: rule.strategy,
															label: t(STRATEGIES.find(s => s.value === rule.strategy)!.label)
														}
														: null
												}
												onChange={(opt: any) => setRule(r => ({ ...r, strategy: opt!.value }))}
												classNamePrefix="react-select"
												isSearchable={false}
											/>
											{rule.strategy && (
												<div className="mt-2">
													<small className="text-muted">
														{t(STRATEGY_HELPERS[rule.strategy])}
													</small>
												</div>
											)}

										</div>
									</>
								)}
								{step === 1 && (
									<Filter
										dataFields={columns} // replace with actual fields
										updateGroups={(groups) => setRule(r => ({ ...r, filters: groups }))}
										groups={rule.filters}
										t={t}
									/>
								)}
								{step === 2 && (
									<DynamicAssignment
										metaDataFields={metaDataFields}
										users={users}
										roles={roles}
										teams={teams}
										assignments={rule.assignments}
										onUpdate={(key: any, input: any) => setRule((r) => ({
											...r,
											assignments: { ...r.assignments, [key]: input }
										}))}
										t={t}
									/>
								)}
							</CardBody>
						</Card>
					</Col>
					<Col lg={4}>
						{renderSummary()}
					</Col>
				</Row>
			</ModalBody>
			<ModalFooter className="border-top shadow-sm px-4 py-3 d-flex justify-content-between align-items-center">
				<Button
					color="outline-secondary"
					className="px-4 py-2"
					disabled={step === 0}
					onClick={() => setStep(step - 1 as 0 | 1 | 2)}
				>
					<i className="ri-arrow-left-line me-1" /> {t('common.offcanvas.import_back')}
				</Button>
				{step < 2 ? (
					<Button
						color="primary"
						className="px-4 py-2"
						onClick={() => setStep(step + 1 as 0 | 1 | 2)}
					>
						{t('common.offcanvas.import_next')} <i className="ri-arrow-right-line ms-1" />
					</Button>
				) : (
					<Button
						color="success"
						className="px-4 py-2"
						onClick={() => onSave(rule)}
						disabled={!rule.name.trim()}
					>
						<i className="ri-check-double-line me-1" /> {t('common.offcanvas.import_finish')}
					</Button>
				)}
			</ModalFooter>

		</Modal>
	);
};

export default withTranslation()(AutoAssignRuleWizard);
